// MessageOriginChip — Sprint agent-message-attribution (D3, D4, D8).
//
// Owns the store selector for the live agent name (D4: no snapshot, no
// new endpoint — `useAppStore((s) => s.agents[id]?.name)`, the pattern
// `PermissionCard.tsx:157` already uses). `UserMessage` stays store-free;
// this component is the single place that reads `agents[id]`.
//
// Mapping (D3, closed): 'founder' | null | undefined | any unrecognised
// value → renders nothing (fail-closed to today's render — an unknown
// origin must never invent a chip). 'system' → muted Pill + Cog +
// "Sistema"/"System". 'wakeup' → muted Pill + Bell + "Despertador"/
// "Wake-up". `fleet:<id>` → info Pill + Bot + the live-resolved name, or
// (D4) an 8-char short id with `title` = the full uuid when the agent is
// not in the store (hard-deleted/internal — the client cannot recover
// that name today, per D4's declared-honest limitation).
//
// Truncation (D5): CSS `truncate` inside the existing `max-w-todo-pill`
// token (`TodoUpdatePill.tsx:47` precedent) + `title` carrying the full
// name — no new design token.

import { useAppStore } from '../../stores/app.store';
import { useTranslation } from '../../i18n';
import { Pill } from '../atoms/Pill';
import { Icon } from '../atoms/Icon';
import type { MessageOrigin } from '../../types';

export interface MessageOriginChipProps {
  origin: MessageOrigin | null | undefined;
  className?: string;
}

const FLEET_PREFIX = 'fleet:';

function shortAgentId(id: string): string {
  return id.slice(0, 8);
}

export function MessageOriginChip({ origin, className = 'mb-2' }: MessageOriginChipProps) {
  const { t } = useTranslation();
  const fleetAgentId =
    typeof origin === 'string' && origin.startsWith(FLEET_PREFIX)
      ? origin.slice(FLEET_PREFIX.length)
      : null;
  // Selector runs unconditionally (rules of hooks); `fleetAgentId` is
  // null for every non-fleet origin, so the lookup is a cheap miss.
  const agentName = useAppStore((s) =>
    fleetAgentId ? s.agents[fleetAgentId]?.name : undefined,
  );

  if (origin === 'system') {
    return (
      <Pill
        color="muted"
        icon={<Icon name="Cog" size={12} aria-hidden="true" />}
        className={className}
      >
        {t('message.origin.system')}
      </Pill>
    );
  }

  if (origin === 'wakeup') {
    return (
      <Pill
        color="muted"
        icon={<Icon name="Bell" size={12} aria-hidden="true" />}
        className={className}
      >
        {t('message.origin.wakeup')}
      </Pill>
    );
  }

  if (fleetAgentId) {
    const known = typeof agentName === 'string' && agentName.length > 0;
    const label = known
      ? agentName
      : t('message.origin.agentFallback', { id: shortAgentId(fleetAgentId) });
    const titleText = known ? agentName : fleetAgentId;
    return (
      <Pill
        color="info"
        icon={<Icon name="Bot" size={12} aria-hidden="true" />}
        className={`${className} max-w-full`}
      >
        <span className="truncate max-w-todo-pill" title={titleText}>
          {label}
        </span>
      </Pill>
    );
  }

  // founder / null / undefined / unrecognised string → fail closed.
  return null;
}
